/**
 * Partner Application Form
 *
 * Handles client-side validation, categorized file uploads via presigned URLs,
 * and form submission to the Supabase Edge Functions.
 * Zero external dependencies.
 */

(function () {
  'use strict';

  // =========================================================================
  // Validation Rules (mirroring the Zod schema on the backend)
  // =========================================================================

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var PHONE_RE = /^\+[1-9]\d{1,14}$/;
  var PLZ_RE = /^\d{5}$/;

  var VALIDATION_MESSAGES = {
    required: 'Dieses Feld ist erforderlich.',
    euRegulation: 'Bitte bestätigen Sie, dass Sie die rechtlichen Vorgaben erfüllen.',
    email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    phone: 'Bitte im Format +49... eingeben (z.B. +491701234567).',
    plz: 'Bitte eine gültige 5-stellige Postleitzahl eingeben.',
    positiveNumber: 'Bitte einen positiven Wert eingeben.',
    privacy: 'Bitte stimmen Sie der Datenschutzerklärung zu.',
    fileType: 'Dateityp nicht erlaubt. Erlaubt: PDF, JPEG, PNG, WebP.',
    fileSize: 'Datei ist zu groß (max. 20 MB).',
    fileRequired: 'Bitte laden Sie eine Datei hoch.',
    filesTooMany: 'Maximale Dateianzahl überschritten.',
  };

  /** @type {Record<string, {required?: boolean, type?: string, isNumber?: boolean}>} */
  var FIELD_RULES = {
    firstname: { required: true },
    lastname: { required: true },
    company: {},
    ust_id: {},
    email: { required: true, type: 'email' },
    phone: { required: true, type: 'phone' },
    address_line_1: { required: true },
    postal_code: { required: true, type: 'plz' },
    city: { required: true },
    state: { required: true },
    service_postal_code: { required: true, type: 'plz' },
    service_radius: { required: true, type: 'positiveNumber', isNumber: true },
    annual_installation_amount: { type: 'positiveNumber', isNumber: true },
    contact_email: { required: true, type: 'email' },
    contact_phone: { required: true, type: 'phone' },
    bank_account_holder: {},
    iban: {},
    bic: {},
  };

  var FILE_CATEGORIES = {
    gewerbe: { required: true, maxFiles: 1 },
    zertifizierung: { required: true, maxFiles: 1 },
    sonstige: { required: false, maxFiles: 8 },
  };

  // =========================================================================
  // Utilities
  // =========================================================================

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function escapeHtml(str) {
    var el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  // =========================================================================
  // Form Controller
  // =========================================================================

  class PartnerApplicationForm {
    constructor(formEl, config) {
      this.form = formEl;
      this.config = config;
      this.sectionId = config.sectionId;
      this.apiBaseUrl = (config.apiBaseUrl || '').replace(/\/+$/, '');
      this.isSubmitting = false;

      /** @type {Record<string, File[]>} */
      this.filesByCategory = {
        gewerbe: [],
        zertifizierung: [],
        sonstige: [],
      };

      this.bannerEl = document.getElementById('partner-form-banner-' + this.sectionId);
      this.successEl = document.getElementById('partner-form-success-' + this.sectionId);
      this.submitBtn = document.getElementById('pf-submit-' + this.sectionId);
      this.submitText = document.getElementById('pf-submit-text-' + this.sectionId);

      this.categoryElements = {};
      var self = this;
      Object.keys(FILE_CATEGORIES).forEach(function (cat) {
        self.categoryElements[cat] = {
          fileInput: document.getElementById('pf-files-' + cat + '-' + self.sectionId),
          fileListEl: document.getElementById('pf-file-list-' + cat + '-' + self.sectionId),
          dropzone: document.getElementById('pf-dropzone-' + cat + '-' + self.sectionId),
        };
      });

      this._bindEvents();
    }

    // -----------------------------------------------------------------------
    // Event Binding
    // -----------------------------------------------------------------------

    _bindEvents() {
      var self = this;

      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self._handleSubmit();
      });

      this.form.addEventListener('input', function (e) {
        var name = e.target.name;
        if (name) self._clearFieldError(name);
      });

      this.form.addEventListener('change', function (e) {
        var name = e.target.name;
        if (name) self._clearFieldError(name);
      });

      Object.keys(FILE_CATEGORIES).forEach(function (cat) {
        var els = self.categoryElements[cat];

        if (els.fileInput) {
          els.fileInput.addEventListener('change', function (e) {
            self._addFiles(cat, Array.from(e.target.files));
            e.target.value = '';
          });
        }

        if (els.dropzone) {
          els.dropzone.addEventListener('dragover', function (e) {
            e.preventDefault();
            els.dropzone.classList.add('partner-form__dropzone--active');
          });

          els.dropzone.addEventListener('dragleave', function () {
            els.dropzone.classList.remove('partner-form__dropzone--active');
          });

          els.dropzone.addEventListener('drop', function (e) {
            e.preventDefault();
            els.dropzone.classList.remove('partner-form__dropzone--active');
            if (e.dataTransfer && e.dataTransfer.files) {
              self._addFiles(cat, Array.from(e.dataTransfer.files));
            }
          });
        }
      });
    }

    // -----------------------------------------------------------------------
    // Validation
    // -----------------------------------------------------------------------

    _validateAll() {
      var isValid = true;
      this._clearAllErrors();

      for (var name in FIELD_RULES) {
        var rules = FIELD_RULES[name];
        var el = this.form.elements[name];
        if (!el) continue;

        var value = (el.value || '').trim();

        if (rules.required && !value) {
          this._showFieldError(name, VALIDATION_MESSAGES.required);
          isValid = false;
          continue;
        }

        if (!value) continue;

        if (rules.type === 'email' && !EMAIL_RE.test(value)) {
          this._showFieldError(name, VALIDATION_MESSAGES.email);
          isValid = false;
        } else if (rules.type === 'phone' && !PHONE_RE.test(value)) {
          this._showFieldError(name, VALIDATION_MESSAGES.phone);
          isValid = false;
        } else if (rules.type === 'plz' && !PLZ_RE.test(value)) {
          this._showFieldError(name, VALIDATION_MESSAGES.plz);
          isValid = false;
        } else if (rules.type === 'positiveNumber' && (isNaN(Number(value)) || Number(value) <= 0)) {
          this._showFieldError(name, VALIDATION_MESSAGES.positiveNumber);
          isValid = false;
        }
      }

      // Validate required file categories
      var self = this;
      Object.keys(FILE_CATEGORIES).forEach(function (cat) {
        var catConfig = FILE_CATEGORIES[cat];
        if (catConfig.required && self.filesByCategory[cat].length === 0) {
          self._showFieldError('files_' + cat, VALIDATION_MESSAGES.fileRequired);
          isValid = false;
        }
      });

      // EU regulation consent
      var euRegEl = this.form.elements['eu_regulation_consent'];
      if (euRegEl && !euRegEl.checked) {
        this._showFieldError('eu_regulation_consent', VALIDATION_MESSAGES.euRegulation);
        isValid = false;
      }

      // Privacy consent
      var privacyEl = this.form.elements['privacy_consent'];
      if (privacyEl && !privacyEl.checked) {
        this._showFieldError('privacy_consent', VALIDATION_MESSAGES.privacy);
        isValid = false;
      }

      return isValid;
    }

    _showFieldError(name, message) {
      var errorEl = this.form.querySelector('[data-error-for="' + name + '"]');
      if (errorEl) {
        errorEl.textContent = message;
      }

      var control = this._getFieldControl(name);
      if (control) {
        control.classList.add('has-error');
      }
    }

    _clearFieldError(name) {
      var errorEl = this.form.querySelector('[data-error-for="' + name + '"]');
      if (errorEl) {
        errorEl.textContent = '';
      }

      var control = this._getFieldControl(name);
      if (control) {
        control.classList.remove('has-error');
      }
    }

    _clearAllErrors() {
      this.form.querySelectorAll('.partner-form__field-error').forEach(function (el) {
        el.textContent = '';
      });
      this.form.querySelectorAll('.form-control.has-error').forEach(function (el) {
        el.classList.remove('has-error');
      });
    }

    _getFieldControl(name) {
      var el = this.form.elements[name];
      if (!el) return null;
      return el.closest('.form-control');
    }

    // -----------------------------------------------------------------------
    // File Handling
    // -----------------------------------------------------------------------

    _addFiles(category, files) {
      this._clearFieldError('files_' + category);
      var allowed = this.config.allowedTypes;
      var maxSize = this.config.maxFileSize;
      var catConfig = FILE_CATEGORIES[category];

      for (var i = 0; i < files.length; i++) {
        var file = files[i];

        if (this.filesByCategory[category].length >= catConfig.maxFiles) {
          if (catConfig.maxFiles === 1) {
            this.filesByCategory[category] = [];
          } else {
            this._showFieldError('files_' + category, VALIDATION_MESSAGES.filesTooMany);
            break;
          }
        }

        if (!allowed.includes(file.type)) {
          this._showFieldError('files_' + category, file.name + ': ' + VALIDATION_MESSAGES.fileType);
          continue;
        }
        if (file.size > maxSize) {
          this._showFieldError('files_' + category, file.name + ': ' + VALIDATION_MESSAGES.fileSize);
          continue;
        }

        var isDuplicate = this.filesByCategory[category].some(
          function (f) { return f.name === file.name && f.size === file.size; }
        );
        if (!isDuplicate) {
          this.filesByCategory[category].push(file);
        }
      }

      this._renderFileList(category);
    }

    _removeFile(category, index) {
      this.filesByCategory[category].splice(index, 1);
      this._renderFileList(category);
      this._clearFieldError('files_' + category);
    }

    _renderFileList(category) {
      var els = this.categoryElements[category];
      if (!els || !els.fileListEl) return;

      var files = this.filesByCategory[category];
      if (files.length === 0) {
        els.fileListEl.innerHTML = '';
        return;
      }

      var html = '';
      for (var i = 0; i < files.length; i++) {
        var file = files[i];
        html += '<div class="partner-form__file-item" data-category="' + category + '" data-file-index="' + i + '">' +
          '<div class="partner-form__file-info">' +
          '<span class="partner-form__file-name">' + escapeHtml(file.name) + '</span>' +
          '<span class="partner-form__file-size">' + formatFileSize(file.size) + '</span>' +
          '</div>' +
          '<button type="button" class="partner-form__file-remove" data-remove-category="' + category + '" data-remove-index="' + i + '" aria-label="Datei entfernen">&times;</button>' +
          '</div>';
      }

      els.fileListEl.innerHTML = html;

      var self = this;
      els.fileListEl.querySelectorAll('.partner-form__file-remove').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var cat = this.getAttribute('data-remove-category');
          var idx = parseInt(this.getAttribute('data-remove-index'), 10);
          self._removeFile(cat, idx);
        });
      });
    }

    _getAllFiles() {
      var all = [];
      var categories = [];
      var self = this;
      Object.keys(FILE_CATEGORIES).forEach(function (cat) {
        self.filesByCategory[cat].forEach(function (file) {
          all.push(file);
          categories.push(cat);
        });
      });
      return { files: all, categories: categories };
    }

    /**
     * Upload all categorized files and return their IDs.
     * @returns {Promise<string[]>}
     */
    async _uploadFiles() {
      var allData = this._getAllFiles();
      var allFiles = allData.files;

      if (allFiles.length === 0) {
        throw new Error('Bitte laden Sie die erforderlichen Dokumente hoch.');
      }

      var fileMeta = allFiles.map(function (f) {
        return { name: f.name, size: f.size, type: f.type };
      });

      var urlResponse = await this._apiRequest(this.apiBaseUrl + '/get-upload-urls', {
        context: 'application',
        files: fileMeta,
      });

      if (!urlResponse.success) {
        var errMsg = 'Fehler beim Vorbereiten der Datei-Uploads.';
        if (urlResponse.errors && urlResponse.errors.length > 0) {
          errMsg = urlResponse.errors.map(function (e) { return e.message; }).join(', ');
        } else if (urlResponse.error) {
          errMsg = urlResponse.error;
        }
        throw new Error(errMsg);
      }

      var uploads = urlResponse.uploads;
      if (!uploads || uploads.length !== allFiles.length) {
        throw new Error('Unerwartete Antwort vom Server beim Datei-Upload.');
      }

      this._showFileUploadProgress();

      var self = this;
      var uploadPromises = uploads.map(function (upload, index) {
        return self._uploadSingleFile(allFiles[index], upload, index);
      });

      var results = await Promise.allSettled(uploadPromises);

      var fileIds = [];
      var errors = [];

      results.forEach(function (result, index) {
        var itemEl = self._getFileItemByGlobalIndex(index);
        if (result.status === 'fulfilled') {
          fileIds.push(result.value);
          if (itemEl) self._setFileItemStatus(itemEl, 'done', 'Hochgeladen');
        } else {
          var reason = result.reason ? result.reason.message : 'Unbekannt';
          errors.push(allFiles[index].name + ': ' + reason);
          if (itemEl) self._setFileItemStatus(itemEl, 'error', 'Fehlgeschlagen');
        }
      });

      if (errors.length > 0) {
        throw new Error('Einige Dateien konnten nicht hochgeladen werden: ' + errors.join('; '));
      }

      return fileIds;
    }

    _getFileItemByGlobalIndex(globalIndex) {
      var counter = 0;
      var self = this;
      var cats = Object.keys(FILE_CATEGORIES);
      for (var c = 0; c < cats.length; c++) {
        var cat = cats[c];
        var files = self.filesByCategory[cat];
        for (var i = 0; i < files.length; i++) {
          if (counter === globalIndex) {
            var els = self.categoryElements[cat];
            if (els && els.fileListEl) {
              return els.fileListEl.querySelector('[data-file-index="' + i + '"]');
            }
            return null;
          }
          counter++;
        }
      }
      return null;
    }

    _uploadSingleFile(file, uploadInfo, index) {
      return new Promise(function (resolve, reject) {
        var xhr = new XMLHttpRequest();

        console.log('[PartnerForm] PUT upload', index, uploadInfo.uploadUrl.substring(0, 120) + '...', file.type, file.size);

        xhr.open('PUT', uploadInfo.uploadUrl, true);
        xhr.setRequestHeader('Content-Type', file.type);

        var itemEl = this._getFileItemByGlobalIndex(index);
        var progressEl = itemEl ? itemEl.querySelector('.partner-form__progress-bar') : null;

        xhr.upload.addEventListener('progress', function (e) {
          if (e.lengthComputable && progressEl) {
            var pct = Math.round((e.loaded / e.total) * 100);
            progressEl.style.width = pct + '%';
          }
        });

        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(uploadInfo.fileId);
          } else {
            var body = '';
            try { body = xhr.responseText; } catch (_) {}
            console.error('[PartnerForm] Upload failed:', xhr.status, body.substring(0, 500));
            reject(new Error('HTTP ' + xhr.status + (body ? ': ' + body.substring(0, 200) : '')));
          }
        });

        xhr.addEventListener('error', function () {
          console.error('[PartnerForm] Upload network error for file', index);
          reject(new Error('Netzwerkfehler'));
        });

        xhr.addEventListener('timeout', function () {
          reject(new Error('Zeitüberschreitung'));
        });

        xhr.timeout = 120000;
        xhr.send(file);
      }.bind(this));
    }

    _showFileUploadProgress() {
      var self = this;
      Object.keys(FILE_CATEGORIES).forEach(function (cat) {
        var els = self.categoryElements[cat];
        if (!els || !els.fileListEl) return;

        els.fileListEl.querySelectorAll('.partner-form__file-item').forEach(function (item) {
          var removeBtn = item.querySelector('.partner-form__file-remove');
          if (removeBtn) removeBtn.remove();

          var statusEl = document.createElement('div');
          statusEl.className = 'partner-form__file-status partner-form__file-status--uploading';
          statusEl.textContent = 'Hochladen...';
          item.appendChild(statusEl);

          var progressWrap = document.createElement('div');
          progressWrap.className = 'partner-form__progress';
          progressWrap.innerHTML = '<div class="partner-form__progress-bar"></div>';
          item.appendChild(progressWrap);
        });
      });
    }

    _setFileItemStatus(itemEl, status, text) {
      var statusEl = itemEl.querySelector('.partner-form__file-status');
      if (statusEl) {
        statusEl.className = 'partner-form__file-status partner-form__file-status--' + status;
        statusEl.textContent = text;
      }
      var progressEl = itemEl.querySelector('.partner-form__progress');
      if (progressEl && status !== 'uploading') {
        progressEl.remove();
      }
    }

    // -----------------------------------------------------------------------
    // Form Submission
    // -----------------------------------------------------------------------

    async _handleSubmit() {
      if (this.isSubmitting) return;

      this._hideBanner();

      if (!this._validateApiUrl()) return;
      if (!this._validateAll()) {
        this._scrollToFirstError();
        return;
      }

      this.isSubmitting = true;
      this._setLoading(true);

      try {
        var allData = this._getAllFiles();
        var fileIds = [];
        if (allData.files.length > 0) {
          fileIds = await this._uploadFiles();
        }

        var payload = this._buildPayload(fileIds);
        var response = await this._apiRequest(this.apiBaseUrl + '/submit-application', payload);

        if (response.success) {
          this.form.classList.add('partner-form--hidden');
          this.successEl.classList.remove('partner-form--hidden');
          this.successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          var message = 'Beim Absenden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';

          if (response.errors && response.errors.length > 0) {
            var self = this;
            response.errors.forEach(function (err) {
              if (err.field && FIELD_RULES[err.field] !== undefined) {
                self._showFieldError(err.field, err.message);
              }
            });
            message = 'Bitte korrigieren Sie die markierten Felder.';
            this._scrollToFirstError();
          } else if (response.error) {
            message = response.error;
          }

          this._showBanner('error', message);
        }
      } catch (error) {
        console.error('[PartnerForm] Submission error:', error);
        this._showBanner('error', error.message || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      } finally {
        this.isSubmitting = false;
        this._setLoading(false);
      }
    }

    _buildPayload(fileIds) {
      var data = {};

      for (var name in FIELD_RULES) {
        var el = this.form.elements[name];
        if (!el) continue;

        var value = (el.value || '').trim();
        var rules = FIELD_RULES[name];

        if (rules.isNumber && value) {
          data[name] = Number(value);
        } else if (value) {
          data[name] = value;
        }
      }

      data.country = 'de';

      if (fileIds.length > 0) {
        data.fileIds = fileIds;
      }

      return data;
    }

    _validateApiUrl() {
      if (!this.apiBaseUrl) {
        this._showBanner('error', 'Die API-URL ist nicht konfiguriert. Bitte kontaktieren Sie den Administrator.');
        return false;
      }
      return true;
    }

    // -----------------------------------------------------------------------
    // API Communication
    // -----------------------------------------------------------------------

    async _apiRequest(url, body) {
      console.log('[PartnerForm] POST', url, JSON.stringify(body).substring(0, 500));

      var response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      var json;
      try {
        json = await response.json();
      } catch (_) {
        var text = await response.text().catch(function () { return ''; });
        console.error('[PartnerForm] Non-JSON response:', response.status, text.substring(0, 300));
        throw new Error('Serverfehler (' + response.status + '). Antwort war kein gültiges JSON.');
      }

      if (!response.ok) {
        console.error('[PartnerForm] API error:', response.status, json);
      }

      if (!response.ok && response.status >= 500) {
        throw new Error('Serverfehler (' + response.status + '). Bitte versuchen Sie es später erneut.');
      }

      return json;
    }

    // -----------------------------------------------------------------------
    // UI Helpers
    // -----------------------------------------------------------------------

    _setLoading(loading) {
      if (!this.submitBtn || !this.submitText) return;

      if (loading) {
        this.submitBtn.disabled = true;
        this.submitText.innerHTML = '<span class="partner-form__spinner"></span> Wird gesendet\u2026';
      } else {
        this.submitBtn.disabled = false;
        this.submitText.textContent = 'Bewerbung absenden';
      }
    }

    _showBanner(status, message) {
      if (!this.bannerEl) return;
      this.bannerEl.className = 'banner banner--' + status;
      this.bannerEl.textContent = message;
      this.bannerEl.classList.remove('partner-form--hidden');
      this.bannerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    _hideBanner() {
      if (!this.bannerEl) return;
      this.bannerEl.classList.add('partner-form--hidden');
      this.bannerEl.textContent = '';
    }

    _scrollToFirstError() {
      var firstError = this.form.querySelector('.form-control.has-error, .partner-form__field-error:not(:empty)');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  function readConfigFromElement(el) {
    return {
      sectionId: el.getAttribute('data-section-id'),
      apiBaseUrl: el.getAttribute('data-api-base-url') || '',
      formId: el.getAttribute('data-form-id'),
      maxFiles: parseInt(el.getAttribute('data-max-files'), 10) || 10,
      maxFileSize: parseInt(el.getAttribute('data-max-file-size'), 10) || 20971520,
      allowedTypes: (el.getAttribute('data-allowed-types') || '').split(',').filter(Boolean),
    };
  }

  function initForms() {
    var configEls = document.querySelectorAll('[data-partner-form-config]');

    if (configEls.length === 0) {
      return;
    }

    configEls.forEach(function (configEl) {
      try {
        var config = readConfigFromElement(configEl);
        var formEl = document.getElementById(config.formId);

        if (!formEl) {
          console.warn('[PartnerForm] Form element not found:', config.formId);
          return;
        }

        if (formEl.dataset.initialized) return;
        formEl.dataset.initialized = 'true';

        new PartnerApplicationForm(formEl, config);
      } catch (e) {
        console.error('[PartnerForm] Failed to initialize:', e);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initForms);
  } else {
    initForms();
  }
})();
